import { React, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from "react-redux";
import { useNavigate, Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import {
    Container, Box, Typography, Divider, Grid, Paper, Button, FormControl, TextField, Chip,
    InputLabel, MenuItem, Select, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

import {
    DataGrid,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

import CustomMessage from '../../utils/components/CustomMessage';
import CustomChip from '../../utils/components/CustomChip';

import { formatMoney, getDate } from '../../utils/functions/Utils';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';

const AjusteInventario = (props) => {

    const auth = useSelector((state) => state.auth);
    let navigate = useNavigate();

    const [data, setData] = useState([]); // for table
    const [categorias, setCategorias] = useState([
        { nombre: 'Todo', value: '...' }
    ]);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const [showDetails, setShowDetails] = useState(false);

    const [requestDate, setRequestDate] = useState(DateTime.now());

    const [tipoMovimiento, setTipoMovimiento] = useState('salida');
    const [product, setProduct] = useState({});
    const [descripcion, setDescripcion] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [authKey, setAuthKey] = useState('');
    const [cantidad, setCantidad] = useState(0);
    const [restante, setRestante] = useState(0);

    const [db, setDb] = useState(false);

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' }); // for custom alert message

    const movimientos = ['entrada', 'salida'];

    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Ajuste de Inventario';
    }, [auth])

    // for custom alert message
    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    useEffect(() => {
        getData();
        setTipoMovimiento('salida');
        setCantidad(0);
    }, [])

    const getData = async () => {
        try {
            const res = await axios.get(`/api/configs/company/${auth.user.company}`);
            if (res.status === 200) {
                setCategorias(res.data.categorias);
            }
        } catch (error) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al obtener las categorías de producto, intentelo mas tarde', severity: 'error' } });
            console.log(error);
        }
    }

    const getProducts = async (text) => {
        try {
            const res = await axios.post('/api/products/filter', { data: text });
            if (res.status === 200) {
                setSearchResults(res.data);
                setRequestDate(DateTime.now());
            }
        } catch (error) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al obtener los productos, intentelo mas tarde', severity: 'error' } });
            console.log(error);
        }
    }

    const handleShowDetails = async (id) => {
        try {
            const res = await axios.get(`/api/products/${id}`);
            if (res.status === 200) {
                setData(res.data);
                setProduct(res.data);
                setShowDetails(true);
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al cargar el producto, favor de intentarlo mas tarde', severity: 'error' } });
            console.log(err);
        }
    }

    const handleMovimiento = async (e) => {
        e.preventDefault();
        setDb(true);

        let x = product;
        x.id = product._id;
        x.nombre = product.nombre;
        x.clave = product.clave;
        x.precioUnitario = product.precioUnitario;
        x.costoUnitario = product.costoUnitario;
        x.descripcion = product.descripcion;
        x.imagen = product.imagen;
        x.categoria = product.categoria;
        x.cantidad = cantidad;

        let tmp = {}

        if (tipoMovimiento === 'entrada') {
            tmp = {
                tipoMovimiento: 'entrada',
                motivo: 'ajuste de inventario',
                entrada: {
                    fechaDeEntrada: DateTime.now(),
                    noFactura: 'N/A',
                    recibe: 'N/A',
                    entrega: 'N/A',
                    proveedor: 'N/A',
                },
                descripcion: descripcion,
                observaciones: observaciones,
                productos: [x],
                auth: auth.user.name,
                estatus: 'Consolidado',
            }
        } else {
            tmp = {
                tipoMovimiento: 'salida',
                operacion: 'Ajuste',
                motivo: 'ajuste de inventario',
                salida: {
                    fechaDeSalida: DateTime.now(),
                    solicitante: 'Ajuste',
                    cliente: 'N/A',
                    noOrden: 'S/N',
                },
                descripcion: descripcion,
                observaciones: observaciones,
                productos: [x],
                auth: auth.user.name,
                estatus: 'Consolidado',
            }
        }

        try {
            const res = await axios.post('/api/inventories/', { inventory: tmp, authKey: true, user: auth.user });
            if (res.status === 200) {
                setApiRes(state => { return { ...state, show: true, message: 'Ajuste de inventario registrado', severity: 'success' } });
                getData();
                setTipoMovimiento('salida');
                setCantidad(0);
                setDescripcion('');
                setObservaciones('');
                setDb(false);
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: err.response.data.message, severity: 'error' } });
            console.log(err);
            setDb(false);
        }
    }

    const renderProducts = () => {
        return (
            <>
                <Typography variant='body1' sx={{ margin: '0px 8px', color: 'comp.gray' }}>{searchResults.length} productos encontrados</Typography>
                <Typography variant='body1' sx={{ margin: '0px 8px', color: 'comp.gray' }}>Consulta: {getDate(requestDate, 'DD, HH:mm:ss')} hrs</Typography>
                <DataGrid
                    autoPageSize={true}
                    rows={searchResults.map(({ product }) => product)}
                    density='comfortable'
                    sx={{ height: `${props.height ? props.height : height - 100}px`, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                    // sx={{ height: `${height - 100}px`, '& .MuiDataGrid-row': {backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': {backgroundColor: 'comp.tableHeaderBg', color: 'text.primary'}}}
                    getRowId={(row) => row._id}
                    onCellDoubleClick={(row) => handleShowDetails(row.id)}
                    columns={[
                        { field: 'clave', headerName: 'Clave', maxWidth: 300, minWidth: 200, flex: 1 },
                        // { field: 'cantidad', headerName: 'Existencia', type: 'number', maxWidth: 80, cellClassName: (params) => params.value <= 0 ? 'text-danger text-bold h5' : 'text-success text-bold h5'},
                        { field: 'cantidad', headerName: 'Existencia', type: 'number', maxWidth: 80, cellClassName: (params) => params.value > 10 ? 'text-success text-bold h5' : params.value <= 0 ? 'text-danger text-bold h5' : 'text-warning text-bold h5' },
                        { field: 'nombre', headerName: 'Producto', maxWidth: 200, minWidth: 100, flex: 1 },
                        { field: 'descripcion', headerName: 'Descripción', minWidth: 200, flex: 1 },
                        { field: 'marca', headerName: 'Marca' },
                        { field: 'modelo', headerName: 'Modelo', minWidth: 200, },
                        { field: 'categoria', headerName: 'Categoría', minWidth: 120, },
                        {
                            field: 'id',
                            headerName: 'Acción',
                            headerAlign: 'center',
                            align: 'center',
                            sortable: false,
                            filterable: false,
                            disableColumnMenu: true,
                            renderCell: (row) => (
                                <Link target='_blank' to={{ pathname: '/inventario/editarProducto/' + row.id, state: { id: row.id } }} style={{ textDecoration: 'none' }}>
                                    <Button sx={{ minWidth: '90px' }} variant='contained' color='primary' size='small'>
                                        Editar &nbsp;&nbsp;<i className='fad fa-info-circle'></i>
                                    </Button>
                                </Link>
                            )
                        },
                        {
                            field: 'imagen',
                            headerName: 'Imagen',
                            headerAlign: 'center',
                            align: 'center',
                            sortable: false,
                            filterable: false,
                            disableColumnMenu: true,
                            renderCell: (row) => (
                                <>
                                    <LazyLoadImage src={row.row.imagen} alt={row.row.nombre} style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
                                </>
                            )
                        },
                    ]}
                    pagination
                    slots={{
                        pagination: CustomPagination,
                    }}
                    initialState={{
                        ...data.initialState,
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                />
            </>
        )
    }

    const handleCloseDetails = () => {
        setShowDetails(false);
    };

    const renderDetails = () => {
        return (
            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                className='exo2'
                scroll='paper'
                onClose={handleCloseDetails}
                open={showDetails} >
                <DialogTitle>Detalles del producto</DialogTitle>
                <DialogContent>
                    <Box sx={{ width: '100%', padding: '24px', border: 'solid 1px comp.boxBorder', backgroundColor: 'comp.boxBg', borderRadius: '20px' }}>
                        <form onSubmit={(e) => handleMovimiento(e)}>
                            <Box className='d-flex align-items-stretch justify-content-center mb-2'>
                                <Typography sx={{ fontSize: '1.1em', color: 'comp.spColor2' }} className='px-1 d-flex align-items-center'>Nombre del producto:</Typography>
                                <Typography sx={{ fontSize: '1.1em' }} className='px-1 text-bold'>{data.nombre ? data.nombre : ''}</Typography>
                            </Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} >
                                    <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img alt='product-img' style={{ width: 'auto', maxWidth: '400px', boxShadow: '2px 2px 10px #13161B', borderRadius: '20px' }} src={data.imagen} />
                                    </Box>
                                    <Box className='d-flex align-items-stretch justify-content-center'>
                                        <Typography sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='mt-3 px-1 d-flex align-items-center'>Unidades disponibles:</Typography>
                                        <Typography sx={{ fontSize: '16px' }} className='mt-3 px-1 text-bold'>{data.cantidad ? data.cantidad + ' ' + data.unidad : ''}</Typography>
                                    </Box>
                                    <Box className='d-flex align-items-stretch justify-content-center'>
                                        <Typography sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='mb-3 px-1 d-flex align-items-center'>Clave:</Typography>
                                        <Typography sx={{ fontSize: '16px' }} className='mb-3 px-1 text-bold'>{data.clave}</Typography>
                                    </Box>

                                    <Divider className='my-3' textAlign="left">
                                        <CustomChip variant='outlined' estatus='DIAGNOSTICADO' label='Editar existencias' className='text-bold' />
                                    </Divider>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                        <TextField label="Cantidad de entrada/salida" variant="outlined" value={cantidad} type='number' InputProps={{ inputProps: { min: 0, max: 100, freesolo: 'false' } }} onChange={(e) => setCantidad(e.target.value)} />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                        <InputLabel id="select">Movimiento</InputLabel>
                                        <Select
                                            labelId="select"
                                            value={tipoMovimiento}
                                            label="Movimiento"
                                            onChange={(e) => setTipoMovimiento(e.target.value)}
                                            defaultValue='salida'
                                        >
                                            {movimientos.map((movimiento, i) => (
                                                <MenuItem key={i} value={movimiento}>{movimiento}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                        <TextField disabled label="Cantidad despues de movimiento" variant="outlined" value={restante} />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                        <TextField required label="Descripción" variant="outlined" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                        <TextField label="Observaciones" variant="outlined" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}>
                                    <Button disabled={db} variant='contained' color='primary' className='m-1 p-2' type='submit' >&nbsp;Confirmar movimiento &nbsp;&nbsp;<i className='fad fa-badge-check' style={{ transform: 'scale(1.3)' }}></i>&nbsp;</Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                </DialogContent>
                <DialogActions style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link target='_blank' to={{ pathname: '/inventario/editarProducto/' + data._id, state: { id: data._id } }} style={{ textDecoration: 'none' }}>
                        <Button sx={{ minWidth: '90px' }} variant='contained' color='primary' >
                            Editar &nbsp;&nbsp;<i className='fad fa-edit'></i>
                        </Button>
                    </Link>
                    <Link target='_blank' to={{ pathname: '/inventario/seguimientoProducto/' + data._id, state: { id: data._id } }} style={{ textDecoration: 'none' }}>
                        <Button className='mx-3 my-2' sx={{ minWidth: '90px' }} variant='contained' color='info' >
                            Ver historial de movimientos &nbsp;&nbsp;<i className='fad fa-history'></i>
                        </Button>
                    </Link>
                    <Button className='mx-2' color='warning' variant='contained' onClick={() => setShowDetails(false)}>Cerrar &nbsp; <i className="fad fa-times"></i></Button>
                </DialogActions>
            </Dialog>
        )
    }

    useEffect(() => {
        if (product._id) {
            if (tipoMovimiento === 'entrada')
                setRestante(Number(product.cantidad) + Number(cantidad));
            else
                setRestante(Number(product.cantidad) - Number(cantidad));
        }
    }, [cantidad, tipoMovimiento, product])

    const searchProducts = (e, text) => {
        e.preventDefault();
        setSearchTerm(text)
        getProducts(text);
    }

    return (
        <Container maxWidth='xxl' style={{ marginTop: '10px', padding: '0px 8px' }}>
            <Paper sx={{ padding: '24px 0px' }} elevation={3}>
                <Container maxWidth='xxl' style={{ padding: '8px' }}>
                    <Box className='text-center' >
                        <Typography variant='h5' style={{ fontWeight: 700 }}>Ajuste de Inventario</Typography>
                    </Box>

                    <Container maxWidth='xl'>
                        <Button size='small' variant='outlined' onClick={() => navigate('/inventario')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                    </Container>

                    <Divider className='my-3' textAlign="center">
                        <CustomChip variant='outlined' estatus='AUTORIZADO' label='Buscador de productos' className='text-bold' />
                    </Divider>
                    <form onSubmit={(e) => searchProducts(e, searchTerm)}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <FormControl required sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                    <TextField id='search' label="Buscar..." variant="outlined" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                {categorias.map((c, i) => {
                                    if (i % 2 === 0) {
                                        return <Chip key={i} label={c.nombre} onClick={(e) => searchProducts(e, c.value)} color='info' sx={{ margin: '2px 8px', fontWeight: '500' }} variant='outlined' />
                                    } else {
                                        return <Chip key={i} label={c.nombre} onClick={(e) => searchProducts(e, c.value)} color='secondary' sx={{ margin: '2px 8px', fontWeight: '500' }} variant='outlined' />
                                    }
                                })}
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'end' }}>
                                <Button variant="contained" color="secondary" type='submit'>Buscar &nbsp; <i className="fad fa-sign-in"></i></Button>
                            </Grid>
                        </Grid>
                    </form>

                    {searchResults.length > 0 ? renderProducts() : <Typography variant='h6' className='text-center' style={{ fontWeight: 700 }}>No se encontraron productos</Typography>}
                </Container>
            </Paper>
            <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
            {showDetails ? renderDetails() : <></>}
        </Container>
    )
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default AjusteInventario;